import type { BoardSize } from '../shared/config';

const cache = new Map<BoardSize, number[][]>();

export function neighborsFor(size: BoardSize): number[][] {
  const cached = cache.get(size);
  if (cached) return cached;
  const neighbors = Array.from({ length: size * size }, (_, index) => {
    const row = Math.floor(index / size);
    const column = index % size;
    const result: number[] = [];
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nextRow = row + dr;
        const nextColumn = column + dc;
        if (nextRow >= 0 && nextRow < size && nextColumn >= 0 && nextColumn < size) {
          result.push(nextRow * size + nextColumn);
        }
      }
    }
    return result;
  });
  cache.set(size, neighbors);
  return neighbors;
}

/** Shared traversal; membership checks do not need to allocate a tile path. */
function searchBoard(board: readonly string[], word: string, size: BoardSize, path?: number[]): boolean {
  const neighbors = neighborsFor(size);
  const visit = (index: number, used: number, position: number): boolean => {
    const tile = board[index];
    if (tile === undefined || (path && !tile) || !word.startsWith(tile, position)) return false;
    position += tile.length;
    path?.push(index);
    if (position === word.length) return true;
    used |= 1 << index;
    for (const next of neighbors[index] ?? []) {
      if (!(used & (1 << next)) && visit(next, used, position)) return true;
    }
    path?.pop();
    return false;
  };
  return board.some((_, index) => visit(index, 0, 0));
}

/** Test board geometry independently from dictionary membership. */
export function isWordOnBoard(board: readonly string[], word: string): boolean {
  const size = Math.sqrt(board.length);
  if (!Number.isInteger(size) || size < 1 || size > 5) return false;
  return searchBoard(board, word.toLowerCase(), size as BoardSize);
}

/**
 * Whether a client-traced path is a legal trace of `word`: in-range distinct tiles, each
 * adjacent to the previous, whose letters concatenate to the word. Chaos pickups trust
 * only paths that pass this check.
 */
export function isPathForWord(board: readonly string[], path: readonly number[], word: string): boolean {
  const size = Math.sqrt(board.length);
  if (!Number.isInteger(size) || size < 1 || size > 5 || path.length === 0) return false;
  const neighbors = neighborsFor(size as BoardSize);
  const seen = new Set<number>();
  let spelled = '';
  for (const [position, index] of path.entries()) {
    const tile = board[index];
    if (!Number.isInteger(index) || tile === undefined || seen.has(index)) return false;
    const previous = path[position - 1];
    if (previous !== undefined && !neighbors[previous]!.includes(index)) return false;
    seen.add(index);
    spelled += tile;
  }
  return spelled === word.toLowerCase();
}

/** Find a complete word's tile path for hint highlighting. */
export function findPath(board: readonly string[], word: string): number[] | null {
  if (!word) return null;
  const size = Math.sqrt(board.length) as BoardSize;
  if (![3, 4, 5].includes(size)) return null;
  const path: number[] = [];
  return searchBoard(board, word, size, path) ? path : null;
}
